// "Your household" card on the Plan Defaults page: who is in the household,
// seats, the per-person "let my household see my recipes" switch, and (for
// the owner) sending and revoking invites. Backed by the RPCs in
// supabase/migrations/0041-0042; the design is in
// docs/2026-09-19-household-membership-design.md sections 3, 5 and 6.
//
// Like the share panels, it is self-contained: it reads and mutates through
// db.js directly rather than through MealPlannerContext. It only reads the
// context for who the viewer is and for the People list (planDefaults), which
// the invite gate below needs.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { countPlanUsage } from "../lib/planUtils";
import {
  loadHouseholdSummary,
  loadHouseholdMembers,
  loadHouseholdInvites,
  loadMyPendingHouseholdInvites,
  loadShareRecipesWithHousehold,
  setShareRecipesWithHousehold,
  createHouseholdInvite,
  revokeHouseholdInvite,
  declineHouseholdInvite,
  leaveHousehold,
  removeHouseholdMember,
  sendHouseholdInviteEmail,
} from "../lib/db";
import { COLORS, FONT_SIZE, RADIUS, SPACE } from "../lib/constants";

export function HouseholdPanel() {
  const { userId, householdId, planDefaults, recipes, plansByWeek, sharedWithMeIds, colors } = useMealPlanner();
  const { card, ink, soft, accent, danger, line } = colors;

  const [loaded, setLoaded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]); // this household's live invites (owner only)
  const [myInvites, setMyInvites] = useState([]); // invitations addressed to me
  const [shareRecipes, setShareRecipes] = useState(false);
  const [email, setEmail] = useState("");
  const [gateOpen, setGateOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null); // owner: the member being removed
  const [leaving, setLeaving] = useState(false); // member: the pre-leave step is open
  const [shareIds, setShareIds] = useState(new Set()); // recipes to share with the people staying
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(""); // non-error feedback, e.g. "Invitation emailed to …"

  const reload = useCallback(async () => {
    const [s, m, mine, share] = await Promise.all([
      loadHouseholdSummary().catch(() => null),
      loadHouseholdMembers().catch(() => []),
      loadMyPendingHouseholdInvites().catch(() => []),
      loadShareRecipesWithHousehold(userId, householdId).catch(() => false),
    ]);
    setSummary(s);
    setMembers(m);
    setMyInvites(mine);
    setShareRecipes(share);
    setInvites(s?.role === "owner" ? await loadHouseholdInvites().catch(() => []) : []);
    setLoaded(true);
  }, [userId, householdId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function run(action) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (e) {
      setError(e.message || "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  }

  function toggleShare(value) {
    const previous = shareRecipes;
    setShareRecipes(value);
    run(async () => {
      try {
        await setShareRecipesWithHousehold(userId, householdId, value);
      } catch (e) {
        setShareRecipes(previous);
        throw e;
      }
    });
  }

  // The claim gate (design doc section 4): an unclaimed row's numbers are
  // visible to every member, and existing rows start out unclaimed -- so
  // before the first invite, the owner is warned about any unclaimed row that
  // has numbers in it, and pointed at the People section to claim their own.
  const people = planDefaults.householdMembers || [];
  const hasMine = people.some((p) => p.userId && p.userId === userId);
  const exposedPeople = people.filter(
    (p) => !p.userId && !p.targetsHidden && (p.dailyCalorieTarget > 0 || p.dailyProteinTarget > 0 || p.dailyCarbsTarget > 0 || p.dailyFatTarget > 0)
  );
  const needsGate = !hasMine && exposedPeople.length > 0;

  function sendInvite() {
    return run(async () => {
      const to = email.trim();
      const inviteId = await createHouseholdInvite(to);
      setEmail("");
      setGateOpen(false);
      // The invite exists either way; the email is a courtesy on top of it, so
      // a failure here must not read as a failed invite.
      try {
        await sendHouseholdInviteEmail(inviteId);
        setNotice(`Invitation emailed to ${to}.`);
      } catch (e) {
        setNotice(`Invite created for ${to}, but the email didn't go out (${e.message}) — tell them to sign in at ${window.location.origin} with that address.`);
      }
      await reload();
    });
  }

  // Send / resend for an existing invite. The server enforces the limits (3
  // sends, 5 minutes apart); this only decides whether to offer the button.
  function canEmail(invite) {
    return (invite.email_send_count || 0) < 3 && (!invite.emailed_at || Date.now() - new Date(invite.emailed_at).getTime() > 5 * 60 * 1000);
  }

  function emailInvite(invite) {
    return run(async () => {
      await sendHouseholdInviteEmail(invite.id);
      setNotice(`Invitation emailed to ${invite.email}.`);
      await reload();
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    if (needsGate && !gateOpen) {
      setGateOpen(true);
      return;
    }
    sendInvite();
  }

  function reviewPeople() {
    setGateOpen(false);
    document.getElementById("people-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // How much of the household plan rests on whose recipes (see countPlanUsage).
  const usage = useMemo(() => countPlanUsage(plansByWeek), [plansByWeek]);
  // A member leaving: their own recipes that this household plans with.
  const myPlanRecipes = useMemo(() => recipes.filter((r) => r.ownerUserId === userId && usage.has(r.id)), [recipes, userId, usage]);
  // The owner removing someone: that person's planned recipes the owner would
  // lose sight of. Public recipes and ones already shared with the owner stay
  // visible. (v1 has two seats, so "the owner" is the only person staying; with
  // more seats this would need every remaining member's shares.)
  function removalImpact(targetUserId) {
    const affected = recipes.filter((r) => r.ownerUserId === targetUserId && !r.isPublic && !sharedWithMeIds.has(r.id) && usage.has(r.id));
    return { recipes: affected.length, slots: affected.reduce((n, r) => n + usage.get(r.id).slots, 0) };
  }

  if (!loaded || !summary) return null;

  const isOwner = summary.role === "owner";
  const seatsUsed = summary.memberCount + summary.pendingInviteCount;
  const hasFreeSeat = seatsUsed < summary.seatLimit;
  const smallButton = { background: "transparent", border: `1px solid ${line}`, color: danger, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm };

  return (
    <div id="household-section" style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xxl) var(--space-xxl)", marginTop: SPACE.x3, maxWidth: 560 }}>
      <label style={{ fontSize: FONT_SIZE.base, fontWeight: 700, display: "block", marginBottom: SPACE.xs }}>Your household</label>
      <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-xl)" }}>
        {summary.memberCount === 1 ? "Just you so far." : "People who share this household's plans, grocery list and settings."} {seatsUsed} of {summary.seatLimit} seats used.
      </p>

      <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.sm, marginBottom: SPACE.xl2 }}>
        {members.map((m) => (
          <div key={m.user_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, flexWrap: "wrap", fontSize: FONT_SIZE.base, color: ink }}>
            <span>
              {m.email}
              <span style={{ color: soft }}>
                {m.role === "owner" ? " — owner" : ""}
                {m.is_me ? " (you)" : ""}
              </span>
            </span>
            {isOwner && !m.is_me && (
              <button className="mp-btn" disabled={busy} onClick={() => setRemovingId(removingId === m.user_id ? null : m.user_id)} style={smallButton}>
                Remove
              </button>
            )}
          </div>
        ))}
        {invites.map((i) => (
          <div key={i.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, flexWrap: "wrap", fontSize: FONT_SIZE.base, color: ink }}>
            <span>
              {i.email}
              <span style={{ color: soft }}> — invited, not joined yet · {i.emailed_at ? "emailed" : "not emailed"}</span>
            </span>
            <span style={{ display: "flex", gap: SPACE.sm }}>
              {canEmail(i) && (
                <button className="mp-btn" disabled={busy} onClick={() => emailInvite(i)} style={{ ...smallButton, color: accent }}>
                  {i.emailed_at ? "Resend email" : "Send email"}
                </button>
              )}
              <button className="mp-btn" disabled={busy} onClick={() => run(async () => { await revokeHouseholdInvite(i.id); await reload(); })} style={smallButton}>
                Revoke
              </button>
            </span>
          </div>
        ))}
      </div>

      {isOwner && removingId && (() => {
        const target = members.find((m) => m.user_id === removingId);
        if (!target) return null;
        const impact = removalImpact(removingId);
        return (
          <div className="mp-sans" style={{ background: COLORS.card, border: `1px solid ${danger}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)", marginBottom: SPACE.xl2, fontSize: FONT_SIZE.base, color: ink }}>
            <p style={{ margin: "0 0 var(--space-sm)", fontWeight: 700 }}>Remove {target.email} from your household?</p>
            <p style={{ margin: "0 0 var(--space-md)", fontSize: FONT_SIZE.sm, color: soft }}>
              They lose access to this household's plans, grocery list and settings, and their person entry and nutrition targets here are deleted. Their recipes stay theirs.
              {impact.slots > 0
                ? ` ${impact.slots} planned dish${impact.slots === 1 ? "" : "es"} use ${impact.recipes} of their recipe${impact.recipes === 1 ? "" : "s"}. Those stay readable to you (view only) for as long as they're in your plans, because planning with a recipe shares it, so nothing in your plan breaks.`
                : " None of your planned meals use their recipes."}
              {" "}Any of their recipes that aren't in one of your plans will disappear from your library. This frees their seat.
            </p>
            <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
              <button
                className="mp-btn"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await removeHouseholdMember(removingId);
                    setRemovingId(null);
                    await reload();
                  })
                }
                style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
              >
                Remove {target.email}
              </button>
              <button className="mp-btn" disabled={busy} onClick={() => setRemovingId(null)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}>
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      <label className="mp-sans" style={{ display: "flex", alignItems: "flex-start", gap: SPACE.md, fontSize: FONT_SIZE.base, color: ink, marginBottom: SPACE.xs }}>
        <input type="checkbox" className="mp-check" checked={shareRecipes} disabled={busy} onChange={(e) => toggleShare(e.target.checked)} style={{ marginTop: SPACE.hair }} />
        <span>
          Let people in my household see my recipes
          <span style={{ display: "block", fontSize: FONT_SIZE.sm, color: soft }}>
            View only — they can't edit or delete them. Turn it off any time; if someone leaves the household, your recipes stay yours.
          </span>
        </span>
      </label>

      {!isOwner && (
        <div className="mp-sans" style={{ borderTop: `1px dashed ${line}`, marginTop: SPACE.xl2, paddingTop: SPACE.xl2 }}>
          {!leaving ? (
            <button className="mp-btn" disabled={busy} onClick={() => setLeaving(true)} style={smallButton}>
              Leave this household
            </button>
          ) : (
            <div style={{ background: COLORS.card, border: `1px solid ${danger}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)", fontSize: FONT_SIZE.base, color: ink }}>
              <p style={{ margin: "0 0 var(--space-sm)", fontWeight: 700 }}>Leave this household?</p>
              <p style={{ margin: "0 0 var(--space-md)", fontSize: FONT_SIZE.sm, color: soft }}>
                You'll lose access to its plans, grocery list and settings, and your person entry and nutrition targets here are deleted. Your recipes stay yours and go with you, with one exception: any recipe of yours that's placed in one of this household's plans (past or future) stays readable to the people staying, view only, for as long as it's in the plan. Planning with a recipe shares it. Sharing a recipe below keeps it available to them even after it's taken out of the plan; you can stop sharing later from the recipe's page (a shared recipe is view-only while it's shared).
              </p>
              {myPlanRecipes.length > 0 ? (
                <div style={{ marginBottom: SPACE.lg }}>
                  <div style={{ fontSize: FONT_SIZE.sm, fontWeight: 700, marginBottom: SPACE.xs }}>Your recipes this household plans with. They stay readable while planned; share one to keep it after it's removed from the plan:</div>
                  {myPlanRecipes.map((r) => (
                    <label key={r.id} style={{ display: "flex", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.base, marginBottom: SPACE.hair }}>
                      <input
                        type="checkbox"
                        className="mp-check"
                        checked={shareIds.has(r.id)}
                        onChange={(e) =>
                          setShareIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            return next;
                          })
                        }
                      />
                      <span>
                        {r.title}
                        <span style={{ color: soft }}> (planned {usage.get(r.id).slots} time{usage.get(r.id).slots === 1 ? "" : "s"})</span>
                      </span>
                    </label>
                  ))}
                  <button
                    type="button"
                    className="mp-btn"
                    onClick={() => setShareIds(shareIds.size === myPlanRecipes.length ? new Set() : new Set(myPlanRecipes.map((r) => r.id)))}
                    style={{ background: "transparent", color: accent, fontSize: FONT_SIZE.sm, padding: "var(--space-xs) 0", textDecoration: "underline" }}
                  >
                    {shareIds.size === myPlanRecipes.length ? "Select none" : "Select all"}
                  </button>
                </div>
              ) : (
                <p style={{ margin: "0 0 var(--space-lg)", fontSize: FONT_SIZE.sm, color: soft }}>None of your recipes are in this household's plans, so all of your recipes go with you.</p>
              )}
              <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
                <button
                  className="mp-btn"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await leaveHousehold([...shareIds]);
                      window.location.assign("/");
                    })
                  }
                  style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
                >
                  Leave household
                </button>
                <button
                  className="mp-btn"
                  disabled={busy}
                  onClick={() => {
                    setLeaving(false);
                    setShareIds(new Set());
                  }}
                  style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
                >
                  Stay
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isOwner && (
        <div className="mp-sans" style={{ borderTop: `1px dashed ${line}`, marginTop: SPACE.xl2, paddingTop: SPACE.xl2 }}>
          {hasFreeSeat ? (
            <>
              <form onSubmit={handleSubmit} style={{ display: "flex", gap: SPACE.md, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setGateOpen(false);
                  }}
                  placeholder="Invite someone by email…"
                  style={{ flex: 1, minWidth: 200, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink }}
                />
                <button className="mp-btn" type="submit" disabled={busy || !email.trim()} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl2)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base, fontWeight: 500 }}>
                  Send invite
                </button>
              </form>
              <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "var(--space-md) 0 0" }}>
                We'll email them the invitation. It's for people who don't already have a household on The Weekly Kitchen, and it expires after 14 days.
              </p>
            </>
          ) : (
            <p style={{ fontSize: FONT_SIZE.base, color: soft, margin: 0 }}>Your household is full ({summary.seatLimit} of {summary.seatLimit} seats used).</p>
          )}

          {gateOpen && (
            <div style={{ background: COLORS.card, border: `1px solid ${accent}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)", marginTop: SPACE.xl }}>
              <p style={{ fontSize: FONT_SIZE.base, color: ink, margin: "0 0 var(--space-sm)", fontWeight: 700 }}>Before you invite — claim your row first</p>
              <p style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "0 0 var(--space-lg)" }}>
                Numbers for {exposedPeople.map((p) => p.name || "an unnamed person").join(", ")} aren't claimed by anyone, so whoever joins will be able to see and edit them. If one of those is you, claim it in the People section above and only you will see it.
              </p>
              <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
                <button className="mp-btn" type="button" onClick={reviewPeople} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}>
                  Review People
                </button>
                <button className="mp-btn" type="button" disabled={busy} onClick={sendInvite} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}>
                  Invite anyway
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {myInvites.map((i) => (
        <div key={i.id} className="mp-sans" style={{ background: COLORS.card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl)", marginTop: SPACE.xl2, fontSize: FONT_SIZE.base, color: ink }}>
          <strong>{i.invitedByEmail}</strong> invited you to join their household.
          <div style={{ fontSize: FONT_SIZE.sm, color: soft, margin: "var(--space-xs) 0 var(--space-md)" }}>
            You can't join while you belong to a household of your own, and leaving a household isn't available yet — so for now the only option is to decline.
          </div>
          <button className="mp-btn" disabled={busy} onClick={() => run(async () => { await declineHouseholdInvite(i.id); await reload(); })} style={smallButton}>
            Decline
          </button>
        </div>
      ))}

      {notice && (
        <p className="mp-sans" style={{ color: accent, fontSize: FONT_SIZE.base, margin: "var(--space-lg) 0 0" }}>
          {notice}
        </p>
      )}
      {error && (
        <p className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.base, margin: "var(--space-lg) 0 0" }}>
          {error}
        </p>
      )}
    </div>
  );
}
